<template>
  <div>
    <AppMessage
      v-for="message in accountMessages"
      :key="message.id"
      :class="{'opacity-25': deletingMessage === message.id}"
      variant="alert"
      @close="doDeleteAccountMessage(message)">
      <div v-html="specificMessage(message)" />
    </AppMessage>

    <AppMessage
      v-if="accountFetchError"
      variant="alert"
      @close="accountFetchError = false">
      {{ t('messaging.account.errorFetchingAccount') }}
    </AppMessage>

    <BolExpiringSoonNotification
      v-if="credentialsStatus.isExpiringSoon"
      :credentials="bolCredentials" />
    <BolExpiredNotification
      v-if="credentialsStatus.isExpired"
      :credentials="bolCredentials" />
  </div>
</template>

<script setup lang="ts">
import type {AccountMessage} from '@myparcel-frontend/types';
import {AccountMessageSpecifics, ExternalProviderId} from '@myparcel-frontend/types';
import DOMPurify from 'dompurify';
import {computed, ref, watch} from 'vue';
import {useI18n} from 'vue-i18n';
import {useRoute} from 'vue-router';
import AppMessage from '@/components/ui/AppMessages/AppMessage.vue';
import BolExpiredNotification from '@/components/ui/BolExpiredNotification/BolExpiredNotification.vue';
import BolExpiringSoonNotification from '@/components/ui/BolExpiringSoonNotification/BolExpiringSoonNotification.vue';
import useAccountMessages from '@/composables/useAccountMessages';
import {useExternalCredentials} from '@/composables/useExternalCredentials/useExternalCredentials';
import useShop from '@/composables/useShop/useShop';
import useUiSettings from '@/composables/useUiSettings';

const {t} = useI18n();
const {currentShop} = useShop();
const {accountFetchError} = useUiSettings();
const {getAccountMessages, deleteAccountMessage, accountMessages} = useAccountMessages();
const {getCredentialsStatus, getProviderCredentials} = useExternalCredentials();

const route = useRoute();

const deletingMessage = ref<number>();

const bolCredentials = computed(() => {
  return getProviderCredentials(currentShop.value.id, ExternalProviderId.Bol);
});
const credentialsStatus = computed(() => {
  return getCredentialsStatus(bolCredentials.value);
});

const doDeleteAccountMessage = async (message: AccountMessage): Promise<void> => {
  if (deletingMessage.value) {
    return;
  }

  deletingMessage.value = message.id;
  await deleteAccountMessage(message);
  deletingMessage.value = undefined;
};

const specificMessage = (message: AccountMessage): string => {
  const {code} = message;

  const specific = Object.entries(AccountMessageSpecifics).find(([, value]) => value === code);

  if (specific) {
    return t(`messaging.account.${code}`);
  }

  return DOMPurify.sanitize(message.message);
};

watch(route, () => getAccountMessages(), {immediate: true});
</script>
